import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Badge, Button, Card, Icon, Input, PageHeader, Tabs } from "../../design-system";
import {
  FORMACION_CATEGORIES,
  FORMACION_COURSES,
  completedLessons,
  fmtMinutes,
  inProgressCourses,
  mandatoryPending,
  nextLesson,
  progressOfCourse,
  totalLessons,
  totalVolumes,
} from "./data";
import { CourseThumb, ProgressBar, Stars } from "./atoms";

/**
 * Aprendizaje · Formación — catálogo de cursos.
 * Continúa viendo + obligatorios + biblioteca filtrable. UI-only.
 */
function Formacion() {
  document.title = "Formación | Kronaly";
  const navigate = useNavigate();

  const onOpenCourse = (id) => navigate(`/formacion/${id}`);
  const onGoHistorial = () => navigate("/formacion/historial");
  const onCreate = () => navigate("/formacion/crear");

  const [tab, setTab] = useState("todos");
  const [query, setQuery] = useState("");
  const [type, setType] = useState("todos"); // todos | video | presentation

  const filtered = useMemo(() => {
    return FORMACION_COURSES.filter((c) => {
      if (tab === "obligatorio" && !c.mandatory) return false;
      if (tab !== "todos" && tab !== "obligatorio" && c.categoryId !== tab) return false;
      if (type !== "todos" && c.type !== type) return false;
      if (query && !`${c.title} ${c.instructor} ${c.summary}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [tab, query, type]);

  const enrolled = FORMACION_COURSES.filter((c) => c.enrolled);
  const completed = enrolled.filter((c) => progressOfCourse(c) === 1).length;
  const inProgress = inProgressCourses();
  const mandatory = mandatoryPending();
  const totalHours = Math.round(enrolled.reduce((a, c) => a + c.durationMin * progressOfCourse(c), 0) / 60);

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Acme Industries · Aprendizaje"
        title="Formación"
        description="Continúa donde lo dejaste o descubre nuevas formaciones para tu rol."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="secondary" icon="List" onClick={onGoHistorial}>Mi historial</Button>
            <Button variant="secondary" icon="Inbox">Pedir formación</Button>
            <Button variant="primary" icon="Plus" onClick={onCreate}>Crear formación</Button>
          </div>
        }
      />

      {/* Fila de KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} data-responsive-grid="4">
        <KpiCard label="En progreso" value={inProgress.length} suffix="cursos" />
        <KpiCard label="Completados" value={completed} suffix="cursos" tone="success" />
        <KpiCard label="Obligatorios pendientes" value={mandatory.filter((c) => progressOfCourse(c) < 1).length} suffix={mandatory.length > 0 ? "por terminar" : "—"} tone={mandatory.length > 0 ? "warning" : "neutral"} />
        <KpiCard label="Tiempo formado" value={totalHours} suffix="horas" />
      </div>

      {/* Continúa viendo */}
      {inProgress.length > 0 && (
        <section>
          <SectionTitle eyebrow={`Continúa viendo · ${inProgress.length} ${inProgress.length === 1 ? "curso" : "cursos"}`} title="Sigue donde lo dejaste" />
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(inProgress.length, 3)}, 1fr)`, gap: 14, marginTop: 12 }} data-responsive-grid="3">
            {inProgress.slice(0, 3).map((c) => (
              <ContinueCard key={c.id} course={c} onOpen={() => onOpenCourse(c.id)} />
            ))}
          </div>
        </section>
      )}

      {/* Obligatorios */}
      {mandatory.length > 0 && (
        <section>
          <SectionTitle eyebrow={`Obligatorios · ${mandatory.length} sin completar`} title="No los dejes para el final" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginTop: 12 }} data-responsive-grid="3">
            {mandatory.map((c) => (
              <CourseCard key={c.id} course={c} onOpen={() => onOpenCourse(c.id)} variant="mandatory" />
            ))}
          </div>
        </section>
      )}

      {/* Catálogo */}
      <section>
        <SectionTitle
          eyebrow="Catálogo · todos los cursos"
          title="Explora la biblioteca"
          right={<span style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{filtered.length} cursos</span>}
        />

        <Card padded={false} style={{ marginTop: 12 }}>
          <div style={{ padding: "12px 16px 0" }}>
            <Tabs
              tabs={FORMACION_CATEGORIES.map((c) => ({
                id: c.id,
                label: c.label,
                count: c.id === "todos" ? FORMACION_COURSES.length
                  : c.id === "obligatorio" ? FORMACION_COURSES.filter((x) => x.mandatory).length
                  : FORMACION_COURSES.filter((x) => x.categoryId === c.id).length,
              }))}
              value={tab}
              onChange={setTab}
            />
          </div>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por título, formador o tema…" icon="Search" style={{ flex: 1, maxWidth: 340, minWidth: 220 }} />
            <TypeFilter value={type} onChange={setType} />
            <div style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" icon="Filter">Más filtros</Button>
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }} data-responsive-grid="3">
              {filtered.map((c) => (
                <CourseCard key={c.id} course={c} onOpen={() => onOpenCourse(c.id)} />
              ))}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: "48px 16px", textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 13 }}>
                No hay cursos que coincidan con tu búsqueda.
              </div>
            )}
          </div>
        </Card>
      </section>
    </div>
  );
}

// ----- KPI card -----
function KpiCard({ label, value, suffix, tone }) {
  const fgMap = {
    success: "var(--kr-success-700)",
    warning: "var(--kr-warning-700)",
    info: "var(--kr-info-700)",
    neutral: "var(--kr-fg-strong)",
  };
  const c = fgMap[tone] || "var(--kr-fg-strong)";
  return (
    <Card>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
        <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: c, lineHeight: 1 }}>{value}</span>
        {suffix && <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{suffix}</span>}
      </div>
    </Card>
  );
}

function SectionTitle({ eyebrow, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{eyebrow}</div>
        <h2 style={{ margin: "4px 0 0", fontFamily: "var(--kr-font-display)", fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em", color: "var(--kr-fg-strong)" }}>{title}</h2>
      </div>
      {right}
    </div>
  );
}

function TypeFilter({ value, onChange }) {
  const opts = [
    { id: "todos", label: "Todos", icon: null },
    { id: "video", label: "Vídeo", icon: "Play" },
    { id: "presentation", label: "Presentación", icon: "FileText" },
  ];
  return (
    <div style={{ display: "inline-flex", background: "var(--kr-neutral-100)", borderRadius: 6, padding: 2, gap: 2 }}>
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          style={{
            border: "none",
            background: value === o.id ? "var(--kr-bg-surface)" : "transparent",
            boxShadow: value === o.id ? "var(--kr-shadow-xs)" : "none",
            color: value === o.id ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
            fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 4, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          {o.icon && <Icon name={o.icon} size={12} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ----- Tarjeta grande "continúa viendo" -----
function ContinueCard({ course, onOpen }) {
  const next = nextLesson(course);
  const progress = progressOfCourse(course);
  const remaining = Math.max(0, course.durationMin - course.durationMin * progress);
  return (
    <Card padded={false} style={{ overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer" }} onClick={onOpen}>
      <div style={{ position: "relative" }}>
        <CourseThumb course={course} size="md" showProgress />
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: 999, background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 16px rgba(0,0,0,0.18)" }}>
            <Icon name="Play" size={20} style={{ color: "var(--kr-fg-strong)", marginLeft: 3 }} />
          </div>
        </div>
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {course.mandatory && <Badge tone="warning" dot>Obligatorio</Badge>}
          <Badge tone="brand" variant="soft">En progreso</Badge>
        </div>
        <div style={{ fontFamily: "var(--kr-font-display)", fontSize: 15, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em", lineHeight: 1.25 }}>
          {course.title}
        </div>
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
          Próxima lección · <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>{next.lesson.title}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <ProgressBar value={progress} />
          <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{Math.round(progress * 100)}%</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
            {completedLessons(course)} / {totalLessons(course)} lecciones · queda {fmtMinutes(Math.round(remaining))}
          </span>
          <Button variant="primary" size="sm" iconRight="ArrowRight" onClick={(e) => { e.stopPropagation(); onOpen(); }}>Continuar</Button>
        </div>
      </div>
    </Card>
  );
}

// ----- Tarjeta de curso (catálogo / obligatorio) -----
function CourseCard({ course, onOpen, variant }) {
  const [hover, setHover] = useState(false);
  const progress = progressOfCourse(course);
  const isMandatory = variant === "mandatory";
  return (
    <Card
      padded={false}
      onClick={onOpen}
      style={{
        overflow: "hidden", cursor: "pointer", transition: "transform .15s, box-shadow .15s",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hover ? "var(--kr-shadow-md)" : "var(--kr-shadow-xs)",
      }}
    >
      <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <CourseThumb course={course} showProgress={course.enrolled && progress > 0} />
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {course.mandatory && <Badge tone="warning" dot>Obligatorio</Badge>}
            {course.enrolled && progress === 0 && <Badge tone="info">Apuntado</Badge>}
            {course.enrolled && progress > 0 && progress < 1 && <Badge tone="brand">En progreso</Badge>}
            {progress === 1 && <Badge tone="success" dot>Completado</Badge>}
            {isMandatory && course.deadlineDaysLeft != null && course.deadlineDaysLeft <= 30 && (
              <Badge tone="danger" dot>{course.deadlineDaysLeft} d</Badge>
            )}
          </div>

          <div style={{ fontFamily: "var(--kr-font-display)", fontSize: 15, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em", lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {course.title}
          </div>

          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {course.summary}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto", paddingTop: 6 }}>
            <Avatar name={course.instructor} size={22} />
            <div style={{ fontSize: 11, color: "var(--kr-fg-strong)", fontWeight: 500, lineHeight: 1.2 }}>
              {course.instructor}
              <div style={{ color: "var(--kr-fg-muted)", fontWeight: 400, fontSize: 11 }}>{course.instructorRole}</div>
            </div>
          </div>

          <div style={{ height: 1, background: "var(--kr-border-subtle)", margin: "4px -2px" }} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)" }}>
            <span>{totalVolumes(course)} vol</span>
            <span>{totalLessons(course)} lec</span>
            <span>{fmtMinutes(course.durationMin)}</span>
            <Stars value={course.rating} size={10} />
          </div>

          {course.enrolled && progress > 0 && progress < 1 && (
            <div style={{ marginTop: 4 }}>
              <ProgressBar value={progress} />
            </div>
          )}

          {isMandatory && course.deadline && (
            <div style={{ marginTop: 4, fontSize: 11, color: "var(--kr-warning-700)", background: "var(--kr-status-warning-bg)", padding: "6px 10px", borderRadius: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="AlertTriangle" size={12} style={{ color: "var(--kr-warning-700)" }} />
              <span><strong>Antes del {course.deadline}</strong>{course.deadlineDaysLeft != null && ` · quedan ${course.deadlineDaysLeft} días`}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default Formacion;
